import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import api, { getRoles } from "../../services/api";
import Cookies from "js-cookie";

interface RegisterResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    email: string;
  };
}

interface Role {
  _id: string;
  name: string;
}

const EmpRegister: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name:"",
    email: "",
    password: "",
    role: "",
  });
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);

  const token = Cookies.get("token");

  // Fetch roles on mount
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const { data } = await getRoles();
        if (data.success) {
          setRoles(data.data); // data.data is the array of role objects
        } else {
          toast.error("Failed to fetch roles");
        }
      } catch (err) {
        toast.error("Error fetching roles");
      }
    };
    fetchRoles();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.role) {
      toast.error("Please select a role");
      return;
    }

    try {
      setLoading(true);
      const { data } = await api.post<RegisterResponse>(
        "/new/register",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (data.success) {
        toast.success("Registration successful!");
        setFormData({ name:"",email: "", password: "", role: "" });
      } else {
        toast.error(data.message || "Registration failed");
      }
    } catch (error: any) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error("Registration failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-md">
        <h1 className="text-center mb-8 text-blue-600 text-2xl font-semibold">
          Employee Registration
        </h1>

        <form onSubmit={handleSubmit}>
           <div className="form-group mb-3">
              <label className="block mb-1.5 font-medium">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
                placeholder="Enter employee name"
                className="w-full p-2 rounded-md border border-gray-300"
              />
            </div>
          {/* Email */}
          <div className="form-group mb-3">
            <label className="block mb-1.5 font-medium">Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              required
                placeholder="Enter employee email"
              className="w-full p-2 rounded-md border border-gray-300"
            />
          </div>

          {/* Role Dropdown */}
          <div className="form-group mb-3">
            <label className="block mb-1.5 font-medium">Role</label>
            <select
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
              required
              className="w-full p-2 rounded-md border border-gray-300"
            >
              <option value="">Select a role</option>
              {roles.map((r) => (
                <option key={r._id} value={r.name}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>

          {/* Password */}
          <div className="form-group mb-6">
            <label className="block mb-1.5 font-medium">Password</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              required
              className="w-full p-2 rounded-md border border-gray-300"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full p-3 rounded-md text-lg font-medium transition-all duration-150 ${
              loading
                ? "bg-blue-600 text-white opacity-70 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-[#0c2b4b]"
            }`}
          >
            {loading ? "Registering..." : "Register"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EmpRegister;
